import { z } from 'zod';
import { GenericApi } from './genericApi';
import { defaultTimeZone, emailField, sortablePageParamsSchema } from '#api/common.ts';
import { currentUser } from '#api/auth.ts';
import { setTabClientId } from '#lib/utils/tabClientId.ts';
import { setHubClient } from '#api/eventHub.ts';

export const updateContextSchema = z.object({
    clientId: z.number().optional()
});

export const clientUserSettingsSchema = z.object({
    clientId: z.number(),
    clientName: z.string(),
    isDisabled: z.boolean(),
    roles: z.array(z.string()).optional()
});

export const userSchema = z.object({
    id: z.number().default(0),
    email: z.string().default(''),
    displayName: z.string().default(''),
    userName: z.string().default(''),
    emailConfirmed: z.boolean().default(false),
    associatedClients: z.array(clientUserSettingsSchema).optional(),
    clientId: z.number(),
    clientRoles: z.array(z.string()).optional(),
    isGlobal: z.boolean().default(false),
    globalRoles: z.array(z.string()).optional(),
    isAssignable: z.boolean().default(false),
    isEnabled: z.boolean().default(true),
    hasLenderRestriction: z.boolean().default(false),
    hasProductRestriction: z.boolean().default(false),
    isServiceUser: z.boolean().default(false)
});

export const passwordSchema = z
    .string()
    .refine((val) => val.replace(/\s/g, '').length >= 12, "Must be at least 12 characters (spaces don't count)")
    .refine((val) => /[a-zA-Z]/.test(val), 'Must contain at least 1 letter')
    .refine((val) => /[0-9]/.test(val), 'Must contain at least 1 number');

export const createUserSchema = z.object({
    email: emailField,
    displayName: z.string().min(1, 'Display name is required'),
    password: z.union([z.literal(''), passwordSchema]).default(''),
    roles: z.array(z.string()),
    isAssignable: z.boolean().default(false),
    isEnabled: z.boolean().default(true),
    hasLenderRestriction: z.boolean().default(false),
    hasProductRestriction: z.boolean().default(false)
});

export const createServiceUserSchema = z.object({
    displayName: z.string().trim().min(1, 'Name is required'),
    roles: z.array(z.string()),
    isEnabled: z.boolean().default(true),
    hasLenderRestriction: z.boolean().default(false),
    hasProductRestriction: z.boolean().default(false)
});

export const serviceUserCredentialSchema = z.object({
    id: z.number(),
    apiClientId: z.string(),
    name: z.string(),
    allowedIpRanges: z.array(z.string()).default([]),
    expiresUtc: z.string().nullable(),
    lastUsedUtc: z.string().nullable(),
    lastUsedIpAddress: z.string().nullable(),
    createdUtc: z.string(),
    createdByDisplayName: z.string().nullable()
});

export const createdServiceUserCredentialSchema = serviceUserCredentialSchema.extend({
    clientSecret: z.string()
});

export const credentialNoteMaxLength = 200;

export const credentialNoteSchema = z
    .string()
    .trim()
    .min(1, 'Note is required')
    .max(credentialNoteMaxLength, `Note must be ${credentialNoteMaxLength} characters or fewer`);

export const updateServiceUserCredentialSchema = z.object({
    name: credentialNoteSchema,
    allowedIpRanges: z.array(z.string())
});

export const createServiceUserCredentialSchema = updateServiceUserCredentialSchema.extend({
    expiresUtc: z.string().nullable()
});

export const updateUserSchema = z
    .object({
        email: emailField,
        displayName: z.string().min(1, 'Display name is required'),
        roles: z.array(z.string()),
        isAssignable: z.boolean().default(false),
        isEnabled: z.boolean().default(true),
        hasLenderRestriction: z.boolean().default(false),
        hasProductRestriction: z.boolean().default(false),
        password: z.string(),
        newPassword: passwordSchema.optional(),
        confirmPassword: z.string().optional()
    })
    .partial()
    .superRefine((data, ctx) => {
        if (data.newPassword && !data.password)
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['password'], message: 'Password is required' });
        if (!data.newPassword && (data.password || data.confirmPassword))
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['newPassword'],
                message: 'New password is required'
            });
        if (data.newPassword && data.newPassword !== data.confirmPassword)
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['confirmPassword'], message: 'Passwords do not match' });
        if (data.newPassword && data.password && data.newPassword === data.password)
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['newPassword'],
                message: 'New password must be different from current password'
            });
    });

export const authenticatedUserSchema = z.object({
    id: z.number().default(0),
    email: z.string().default(''),
    displayName: z.string().default(''),
    timeZone: z.string().default(defaultTimeZone),
    clientId: z.number(),
    roles: z.array(z.string()).optional(),
    isGlobal: z.boolean().default(false),
    globalRoles: z.array(z.string()).optional(),
    features: z.array(z.string()).default([]),
    isObligor: z.boolean().default(false),
    isImpersonating: z.boolean().default(false),
    hasLenderRestriction: z.boolean().default(false),
    hasProductRestriction: z.boolean().default(false),
    obligationIds: z.array(z.number()),
    lenderIds: z.array(z.number()).default([]),
    productIds: z.array(z.number()).default([]),
    passwordExpiresInDays: z.number().nullable().optional()
});

export const MfaProvider = z.enum(['Email', 'Authenticator']);
export type MfaProvider = z.infer<typeof MfaProvider>;
export const LoginStatus = z.enum(['Authenticated', 'RequiresTwoFactor', 'InvalidCredentials', 'PasswordExpired']);
export type LoginStatus = z.infer<typeof LoginStatus>;

export const mfaProviderStatusSchema = z.object({
    name: MfaProvider,
    isAvailable: z.boolean(),
    isConfigured: z.boolean()
});
export const mfaStatusResponseSchema = z.object({
    isTwoFactorEnabled: z.boolean().default(false),
    providers: z.array(mfaProviderStatusSchema)
});
export const loginResponseSchema = z.object({
    status: LoginStatus,
    success: z.boolean().default(false),
    user: authenticatedUserSchema.optional(),
    twoFactor: mfaStatusResponseSchema.optional()
});

export const mfaAuthenticatorInfoSchema = z.object({
    authenticatorUri: z.string(),
    sharedKey: z.string().optional()
});

export type MfaStatusResponse = z.infer<typeof mfaStatusResponseSchema>;
export type MfaProviderStatus = z.infer<typeof mfaProviderStatusSchema>;

export const listUserSchema = sortablePageParamsSchema.extend({
    search: z.string().optional()
});

export type User = z.infer<typeof userSchema>;
export type CreateUser = z.infer<typeof createUserSchema>;
export type UpdateUser = z.infer<typeof updateUserSchema>;
export type CreateServiceUser = z.infer<typeof createServiceUserSchema>;
export type ServiceUserCredential = z.infer<typeof serviceUserCredentialSchema>;
export type CreatedServiceUserCredential = z.infer<typeof createdServiceUserCredentialSchema>;
export type CreateServiceUserCredential = z.infer<typeof createServiceUserCredentialSchema>;
export type UpdateServiceUserCredential = z.infer<typeof updateServiceUserCredentialSchema>;
export type ListUser = z.infer<typeof listUserSchema>;
export type UpdateContext = z.infer<typeof updateContextSchema>;
export type ClientUserSettings = z.infer<typeof clientUserSettingsSchema>;
export type AuthenticatedUser = z.infer<typeof authenticatedUserSchema>;
export type LoginResponse = z.infer<typeof loginResponseSchema>;
export type MfaAuthenticatorInfo = z.infer<typeof mfaAuthenticatorInfoSchema>;

export class UserApi extends GenericApi<User, CreateUser, UpdateUser, ListUser> {
    constructor() {
        super('users');
    }

    async resetPassword(id: number, newPassword: string) {
        return await this.fetch.postJSON(
            `${this.route}/${id}/admin-reset-password`,
            { newPassword },
            {
                expectedStatusCodes: [200, 400, 404]
            }
        );
    }

    async createServiceUser(payload: CreateServiceUser) {
        return await this.fetch.postJSON<User>(`${this.route}/service-users`, payload, {
            expectedStatusCodes: [200, 400]
        });
    }

    async listCredentials(userId: number) {
        return await this.fetch.getJSON<ServiceUserCredential[]>(`${this.route}/${userId}/credentials`);
    }

    async createCredential(userId: number, payload: CreateServiceUserCredential) {
        return await this.fetch.postJSON<CreatedServiceUserCredential>(`${this.route}/${userId}/credentials`, payload, {
            expectedStatusCodes: [200, 400, 404]
        });
    }

    async updateCredential(userId: number, id: number, payload: UpdateServiceUserCredential) {
        return await this.fetch.putJSON<ServiceUserCredential>(`${this.route}/${userId}/credentials/${id}`, payload, {
            expectedStatusCodes: [200, 400, 404]
        });
    }

    async deleteCredential(userId: number, id: number) {
        return await this.fetch.delete(`${this.route}/${userId}/credentials/${id}`, {
            expectedStatusCodes: [200, 404]
        });
    }

    async switchClient(context: UpdateContext) {
        const result = await this.fetch.putJSON<AuthenticatedUser>(`auth/switch-client?cookieMode=true`, context, {
            expectedStatusCodes: [403]
        });

        if (result.ok && result.data) {
            // Scope the tab before publishing the user: the ClientId header is read from tab storage per
            // request, so a subscriber reacting to currentUser (the time-travel re-check) would otherwise
            // resolve against the client being switched away from.
            setTabClientId(result.data.clientId);
            setHubClient(result.data.clientId);
            currentUser.set(result.data);
        }

        return result;
    }
}
